import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Facebook, Instagram, Shield, Database, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';

const DataUsagePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Data Usage Policy</h1>
          
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Effective Date:</strong> October 15, 2025<br />
              <strong>Last Updated:</strong> October 15, 2025
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
              <p className="text-blue-800">
                This Data Usage Policy explains how Quixocial collects, processes, and uses data from Facebook, Instagram, and other Meta platforms. This policy is required for Meta app review and complements our Privacy Policy.
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Data We Access from Meta Platforms</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                <div className="flex items-center mb-4">
                  <Facebook className="w-8 h-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-semibold text-blue-900">Facebook Pages Data</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Data Accessed:</h4>
                    <ul className="list-disc list-inside text-blue-800 text-sm space-y-1">
                      <li>Page basic information (name, ID, category)</li>
                      <li>Page access tokens for publishing</li>
                      <li>Page insights and analytics data</li>
                      <li>Post performance metrics</li>
                      <li>Audience demographics</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">Purpose:</h4>
                    <ul className="list-disc list-inside text-blue-800 text-sm space-y-1">
                      <li>Enable content publishing to Facebook Pages</li>
                      <li>Provide analytics and performance reporting</li>
                      <li>Optimize content strategy based on engagement</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                <div className="flex items-center mb-4">
                  <Instagram className="w-8 h-8 text-purple-600 mr-3" />
                  <h3 className="text-xl font-semibold text-purple-900">Instagram Business Data</h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-purple-900 mb-1">Data Accessed:</h4>
                    <ul className="list-disc list-inside text-purple-800 text-sm space-y-1">
                      <li>Business account information (username, ID)</li>
                      <li>Media publishing permissions</li>
                      <li>Instagram insights and analytics</li>
                      <li>Post engagement metrics</li>
                      <li>Story and feed performance data</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-purple-900 mb-1">Purpose:</h4>
                    <ul className="list-disc list-inside text-purple-800 text-sm space-y-1">
                      <li>Publish content to Instagram business accounts</li>
                      <li>Provide comprehensive social media analytics</li>
                      <li>Track content performance across platforms</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. How We Use Meta Platform Data</h2>
            
            <div className="space-y-6 mb-6">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Content Publishing</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Automated Publishing:</strong> Schedule and publish posts to connected Facebook Pages and Instagram accounts</li>
                  <li><strong>Cross-Platform Management:</strong> Manage multiple social media accounts from a single dashboard</li>
                  <li><strong>Content Optimization:</strong> Use engagement data to optimize posting times and content strategy</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Analytics and Reporting</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Performance Tracking:</strong> Monitor post engagement, reach, and impressions</li>
                  <li><strong>Audience Insights:</strong> Analyze audience demographics and behavior patterns</li>
                  <li><strong>ROI Measurement:</strong> Track social media performance and return on investment</li>
                  <li><strong>Comparative Analysis:</strong> Compare performance across different platforms and time periods</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">AI-Powered Features</h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2">
                  <li><strong>Content Generation:</strong> Use engagement data to inform AI content creation</li>
                  <li><strong>Hashtag Optimization:</strong> Analyze hashtag performance for better reach</li>
                  <li><strong>Posting Schedule Optimization:</strong> Determine optimal posting times based on audience activity</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Data Storage and Security</h2>
            
            <div className="bg-gray-900 text-white p-6 rounded-lg mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center mb-3">
                    <Database className="w-6 h-6 text-blue-400 mr-2" />
                    <h3 className="text-lg font-semibold">Data Storage</h3>
                  </div>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Secure AWS cloud infrastructure</li>
                    <li>Data encrypted in transit (TLS) and at rest (AES-256)</li>
                    <li>Role-based access controls</li>
                    <li>Geographic storage restrictions</li>
                  </ul>
                </div>
                <div>
                  <div className="flex items-center mb-3">
                    <Lock className="w-6 h-6 text-green-400 mr-2" />
                    <h3 className="text-lg font-semibold">Token Management</h3>
                  </div>
                  <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
                    <li>Access tokens encrypted and stored securely</li>
                    <li>Automatic token refresh to maintain connection</li>
                    <li>Limited scope permissions for core functionality</li>
                    <li>Regular cleanup of expired tokens</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Data Retention</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h3 className="font-semibold text-yellow-900 mb-2">Active Accounts</h3>
                <p className="text-yellow-800 text-sm">Data retained while user account is active and connected</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h3 className="font-semibold text-red-900 mb-2">Account Deletion</h3>
                <p className="text-red-800 text-sm">Data deleted within 30 days of account deletion or disconnection</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2">Analytics Data</h3>
                <p className="text-blue-800 text-sm">Aggregated analytics data may be retained for up to 2 years</p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. User Control and Rights</h2>
            
            <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Connection Management</h3>
                  <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                    <li>Easy disconnection of social media accounts</li>
                    <li>Selective permissions control</li>
                    <li>Permission review and modification</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Access and Control</h3>
                  <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                    <li>Data export and download options</li>
                    <li>Data deletion requests</li>
                    <li>Privacy preference controls</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Compliance with Meta Policies</h2>
            
            <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg mb-6">
              <div className="flex items-center mb-4">
                <Shield className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className="text-xl font-semibold text-blue-900">Platform Policy Compliance</h3>
              </div>
              <ul className="list-disc list-inside text-blue-800 space-y-2">
                <li><strong>Facebook Platform Policy:</strong> Full compliance with Facebook's platform policies</li>
                <li><strong>Instagram API Terms:</strong> Adherence to Instagram's API terms and conditions</li>
                <li><strong>Content Guidelines:</strong> Respect for Meta's community standards and content policies</li>
                <li><strong>Rate Limiting:</strong> Compliance with API rate limits and usage restrictions</li>
              </ul>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Contact Information</h2>
            <div className="bg-gray-100 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                For questions about this Data Usage Policy or our data practices:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">General Inquiries</h4>
                  <div className="space-y-1 text-gray-700 text-sm">
                    <p><strong>Email:</strong> info@quixocial.com</p>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Data Protection Officer</h4>
                  <div className="space-y-1 text-gray-700 text-sm">
                    <p><strong>Email:</strong> info@quixocial.com</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 p-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg">
              <p className="text-sm text-gray-700 text-center">
                This Data Usage Policy is specifically designed to meet Meta's app review requirements and demonstrates our commitment to responsible data handling and user privacy protection.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataUsagePage;